import { useState, useRef, useCallback } from 'react';

interface UseMediaRecorderReturn {
  isRecording: boolean;
  start: (onDataAvailable: (blob: Blob) => void) => Promise<void>;
  init: (onDataAvailable: (blob: Blob) => void) => Promise<void>;
  startCapture: () => void;
  stop: () => void;
}

export function useMediaRecorder(): UseMediaRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const init = useCallback(async (onDataAvailable: (blob: Blob) => void) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
    const mimeType = mimeTypes.find((mt) => mt === '' || MediaRecorder.isTypeSupported(mt)) || '';

    const mediaRecorder = new MediaRecorder(stream, {
      ...(mimeType ? { mimeType } : {}),
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        onDataAvailable(event.data);
      }
    };

    mediaRecorderRef.current = mediaRecorder;
  }, []);

  const startCapture = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.start(250);
      setIsRecording(true);
    }
  }, []);

  const start = useCallback(async (onDataAvailable: (blob: Blob) => void) => {
    await init(onDataAvailable);
    startCapture();
  }, [init, startCapture]);

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, []);

  return { isRecording, start, init, startCapture, stop };
}